import React, { useState } from 'react';
import classNames from 'classnames';
import { scroller } from 'react-scroll';
import { SubmitHandler, useForm } from 'react-hook-form';
import { MentionsInput, Mention, SuggestionDataItem } from 'react-mentions';

import useMentionableAsset from 'buyside/components/container/Mentionable/useMentionableAsset';
import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import ErrorBoundary from 'buyside/components/presentation/ErrorBoundary/errorBoundary';
import FileUploadWidget, {
  FileType
} from 'buyside/components/presentation/Form/FileUploadWidget/fileUploadWidget';
import ProfileImage, {
  IMAGE_SIZES
} from 'buyside/components/presentation/ProfileImage/profileImage';

import {
  CreateIdeaUpdateCommentMutationVariables,
  IdeaUpdate,
  IdeaUpdateCommentsDocument,
  IdeaUpdateCommentsQuery,
  Maybe,
  useCreateIdeaUpdateCommentMutation
} from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/IdeaUpdates/IdeaUpdateCommentForm/ideaUpdateCommentFormStyles.scss';
import mentionStyling from 'buyside/styles/module/form/mentionStyling';

const IdeaUpdateCommentForm: React.FC<{
  canCommentAnonymously: boolean;
  ideaUpdateId: string;
  isAuthor: boolean;
  isAllocator: boolean;
  isIdeaAnonymous: boolean;
  mentionables: SuggestionDataItem[];
  profileImageUrl?: Maybe<string>;
  isReplying?: boolean;
  parentId?: string;
}> = ({
  canCommentAnonymously,
  ideaUpdateId,
  isAllocator,
  isAuthor,
  isIdeaAnonymous,
  mentionables,
  isReplying,
  parentId,
  profileImageUrl
}): React.ReactElement => {
  const [mentionableAssets, markup, renderSuggestion, displayTransform] = useMentionableAsset();

  const [isFocused, setIsFocused] = useState(false);
  const [commentBody, setCommentBody] = useState('');

  const [createComment, { error, loading }] = useCreateIdeaUpdateCommentMutation({
    update(cache, { data }) {
      cache.modify<IdeaUpdate>({
        id: cache.identify({ id: ideaUpdateId, __typename: 'IdeaUpdate' }),
        fields: {
          commentsCount: cachedCount => (cachedCount || 0) + 1
        }
      });

      const newComment = data?.createComment?.comment;
      if (!newComment) return;

      const cachedQuery = cache.readQuery<IdeaUpdateCommentsQuery>({
        query: IdeaUpdateCommentsDocument,
        variables: { id: ideaUpdateId, controlName: !isAuthor }
      });
      if (!cachedQuery?.ideaUpdate.comments) return;

      cache.writeQuery<IdeaUpdateCommentsQuery>({
        query: IdeaUpdateCommentsDocument,
        data: {
          ...cachedQuery,
          ideaUpdate: {
            ...cachedQuery.ideaUpdate,
            comments: {
              edges: [{ node: newComment }, ...(cachedQuery.ideaUpdate.comments.edges || [])],
              pageInfo: cachedQuery.ideaUpdate.comments.pageInfo
            }
          }
        },
        variables: { id: ideaUpdateId }
      });
    }
  });

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setValue,
    watch
  } = useForm<
    Omit<CreateIdeaUpdateCommentMutationVariables, 'ideaUpdateId' | 'files'> & {
      ideaUpdateId?: string;
      files: FileType[];
    }
  >({
    defaultValues: {
      body: '',
      ideaUpdateId,
      anonymous: (isAllocator && !isAuthor) || (isAuthor && isIdeaAnonymous),
      requestResponse: false,
      files: []
    }
  });

  const prepareFiles = (files: FileType[]): string => {
    const uploadedFiles = files.map(file => {
      const { name, base64 } = file;
      const fileData = file.file;
      return {
        name,
        fileName: fileData.name,
        size: fileData.size,
        data: base64
      };
    });
    return JSON.stringify(uploadedFiles);
  };

  const files = (watch('files') as unknown) as FileType[];

  const onSubmit: SubmitHandler<Omit<
    CreateIdeaUpdateCommentMutationVariables,
    'ideaUpdateId' | 'files'
  > & {
    ideaUpdateId?: string;
    files: FileType[];
  }> = ({ anonymous, requestResponse }): Promise<void> =>
    createComment({
      variables: {
        ideaUpdateId,
        body: commentBody,
        anonymous,
        requestResponse,
        files: prepareFiles(files),
        parentId
      }
    }).then(res => {
      const commentId = res.data?.createComment?.comment?.id;
      setCommentBody('');
      setIsFocused(false);
      return scroller.scrollTo(`update-comment-${commentId}-top`, {
        duration: 500,
        delay: 0,
        smooth: 'easeInOutQuint',
        offset: -(65 + 45 + 15) // -(top navigation height + page navigator height + some padding)
      });
    });

  return (
    <ErrorBoundary pageName="comment form">
      <form
        className={classNames(styles.form, { [styles.isReplying]: isReplying })}
        onFocus={(): void => setIsFocused(true)}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles.formLeft}>
          <ProfileImage source={profileImageUrl} size={IMAGE_SIZES.SMALL} />
        </div>
        <div className={styles.formRight}>
          <MentionsInput
            {...register('body')}
            allowSpaceInQuery
            data-heap="comment-text-field"
            data-testid="ideaUpdateCommentTextArea"
            disabled={isSubmitting || loading}
            id="idea-update-comment-body"
            onChange={(e): void => {
              setCommentBody(e.target.value);
              setValue('body', e.target.value, { shouldDirty: true });
            }}
            placeholder="Post a comment"
            style={mentionStyling}
            value={commentBody}
          >
            <Mention
              appendSpaceOnAdd
              data={mentionables?.filter(Boolean) as SuggestionDataItem[]}
              style={{ backgroundColor: '#DEEDF5' }}
              trigger="@"
            />
            <Mention
              appendSpaceOnAdd
              data={mentionableAssets}
              displayTransform={displayTransform}
              markup={markup}
              renderSuggestion={renderSuggestion}
              style={{ backgroundColor: 'rgb(255, 201, 201)' }}
              trigger="$"
            />
          </MentionsInput>
          {isFocused && (
            <>
              {errors.body && <div className={styles.error}>{errors.body.message}</div>}
              <FileUploadWidget
                data-testid="fileUploadWidget"
                setFieldValue={setValue}
                uploadedFiles={files}
              />
              <div className={styles.fieldNote}>
                <div>
                  <strong>Type '@'</strong> to tag participants in this comment thread, users you
                  follow, or users that are following you. Tagged members will be notified.
                </div>
                <div>
                  <strong>Type '$'</strong> and begin typing to tag any ticker on SumZero. Members
                  who watchlisted the tagged ticker will be notified.
                </div>
              </div>
              <div>
                {!isAuthor && (
                  <label className={styles.checkbox} id="post-update-comment-request-response">
                    <input
                      {...register('requestResponse')}
                      data-heap="request-response"
                      disabled={isSubmitting || loading}
                      id="idea-update-comment-request-response-from-author"
                      type="checkbox"
                    />
                    Request a response from the idea author
                  </label>
                )}
                {((!isAuthor && !isAllocator && canCommentAnonymously) ||
                  (isAuthor && isIdeaAnonymous)) && (
                  <label className={styles.checkbox} id="post-update-comment-anonymously">
                    <input
                      {...register('anonymous')}
                      disabled={isSubmitting || loading}
                      id="anonymous"
                      type="checkbox"
                    />
                    Post comment anonymously
                  </label>
                )}
              </div>
              {error && <div className={styles.error}>{error.graphQLErrors[0].message}</div>}
            </>
          )}
          <div className={styles.buttonArea}>
            <Button
              disabled={!commentBody}
              id="post-update-comment"
              isLoading={isSubmitting || loading}
              size={BUTTON_SIZES.SMALL}
              text={isReplying ? 'Post Reply' : 'Post Comment'}
              type={BUTTON_TYPES.PRIMARY}
            />
          </div>
          {!isAuthor && isAllocator && (
            <div className={styles.allocatorNotice}>
              As an allocator, all of your comments are anonymous.
            </div>
          )}
        </div>
      </form>
    </ErrorBoundary>
  );
};

export default IdeaUpdateCommentForm;
